import { useSelector, useDispatch } from 'react-redux';
import {
  UncontrolledTooltip,
  // Collapse
} from 'reactstrap';
import {
  // useParams,
  NavLink,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { numberFormatter } from '../helpers/formatters';
import { AddToGroup } from './utils/AddToGroup';
import {
  useCreateUpdateScenarioMutation,
  useGetTSPsQuery,
  useLazyGetPointConstraintsQuery,
  useLazyGetPricesQuery,
  useLazyGetScenarioQuery,
  useLazyGetSegmentConstraintsQuery,
  useLockScenarioMutation,
} from '../services/edgeApi';
import { toast } from 'react-hot-toast';
import { toIsoDate } from '../utils/stringUtils';
import {
  scenarioActions,
  selectFilteredChains,
  selectScenario,
} from './scenarioSlice';
import { store } from '../store';
import {
  CopyIcon,
  LinkIcon,
  LockIcon,
  LockUnlockedIcon,
  UploadIcon,
} from '@iconicicons/react';
import { colors } from '../utils/colors';
import { useTspFilter } from '../helpers/hooks';

import * as pathsActions from '../paths/pathsSlice';
// import { OperationDetail } from './OperationDetail';
import * as xlsx from 'xlsx';
import { scenarioToWorkbook } from '../utils/excelUtils';
import {
  selectAllEdges,
  selectAllNodes,
  selectAllPoints,
  selectSortedTsps,
} from '../map/slice/mapSelectors';
import { Market, Operation, Scenario, ScenarioGroup } from '@/types';
import {
  OptimizationProgressPayload,
  OptimizationTaskState,
} from '@/hooks/useAsyncOptimizeScenario';
import { OptimizeScenarioButton } from './utils/OptimizeScenarioButton';
import { UnflowedGasWarnings } from './utils/UnflowedGasWarning';
import { ProgressBar } from '../helpers/ProgressBar';
import { getUniqueChainTsps } from '../utils/chainUtils';
import { useViewMode } from '../hooks/useViewMode';
import { copyToClipboard } from '../utils/clipboardUtils';
import { DevelopmentFlag } from '../helpers/DevelopmentFlag';
import { AnalyticsEvent } from '../analytics';
import { useAnalytics } from '../hooks/useAnalytics';

const SECOND = 1000;

export type ScenarioDetailProps = {
  scenarioId: string;
  scenarioGroup?: ScenarioGroup;
  watch: () => (() => any) | undefined;
  asyncOptimize: () => void;
  state: OptimizationTaskState;
  message: string;
  optimizationError?: string | null;
  optimizationProgress?: OptimizationProgressPayload | null;
  onEditStart: () => void;
  onOptimize: (scenario: Scenario) => void;
};

export const ScenarioDetail: React.FC<ScenarioDetailProps> = ({
  scenarioId,
  scenarioGroup,
  state,
  message,
  optimizationError,
  optimizationProgress,
  watch,
  asyncOptimize,
  onEditStart,
  onOptimize,
}) => {
  const [viewMode] = useViewMode();
  const selectedPathId = useSelector(pathsActions.selectActivePathId);
  const setTspFilter = useTspFilter();
  const [createUpdateScenario, { isLoading: isCreatingUpdating }] =
    useCreateUpdateScenarioMutation();
  const [lockScenarioTrigger, { isLoading: isLocking }] =
    useLockScenarioMutation();
  const [pricesTrigger, { isFetching: fetchingPrices }] =
    useLazyGetPricesQuery();
  const [
    segmentConstraintsTrigger,
    { isFetching: fetchingSegmentConstraints },
  ] = useLazyGetSegmentConstraintsQuery();
  const [pointConstraintsTrigger, { isFetching: fetchingPointConstraints }] =
    useLazyGetPointConstraintsQuery();
  // const [showMarketData, setShowMarketData] = useState(false);
  const [shouldLiveUpdate, setShouldLiveUpdate] = useState(false);
  useGetTSPsQuery(undefined);
  const [
    scenarioTrigger,
    { isLoading: isGetScenarioLoading, isFetching: isGetScenarioFetching },
  ] = useLazyGetScenarioQuery();

  const allNodes = useSelector(selectAllNodes);
  const allEdges = useSelector(selectAllEdges);
  const allPoints = useSelector(selectAllPoints);
  const tsps = useSelector(selectSortedTsps);
  const [queryParams, _] = useSearchParams();
  const navigate = useNavigate();
  const analytics = useAnalytics();

  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const scenario = useSelector(selectScenario);
  // const allPathsOperations = useSelector(selectAllPathsOperations);

  const filteredChains = useSelector(selectFilteredChains);

  useEffect(() => {
    if (scenario?.operations) {
      if (scenario.operation_chains.length > 0) {
        dispatch(
          pathsActions.pathSelected({
            selectedPathId: filteredChains[0]?.id,
          }),
        );
        // TODO: use context and map inject instead of redux to make mapping paths more composable and reusable
        dispatch(
          pathsActions.setPathsVisible(
            filteredChains.slice(0, 5).map((chain) => String(chain.id)),
          ),
        );
      } else {
        const maxId =
          scenario.operations.reduce<Operation | null>((result, op) => {
            if (!result || op.delivered_dth > result?.delivered_dth) {
              return op;
            }
            return result;
          }, null)?.id ?? null;
        dispatch(pathsActions.pathSelected({ selectedPathId: maxId }));
        dispatch(
          pathsActions.setPathsVisible(
            scenario.operations.slice(0, 5).map((op) => String(op.id)),
          ),
        );
      }
    }
  }, [scenario.id, scenario.operation_chains.length, scenario.operations]);

  useEffect(() => {
    if (selectedPathId) return;
    if (
      !(Object.keys(allNodes)?.length > 0) ||
      !(Object.keys(allEdges)?.length > 0) ||
      !(Object.keys(allPoints)?.length > 0) ||
      !(tsps?.length > 0)
    )
      return;
    const selectedChain = Number(queryParams.get('selectedChain'));
    if (selectedChain) {
      const chain = scenario.operation_chains.find(
        ({ id }) => Number(id) === selectedChain,
      );
      if (chain) {
        dispatch(pathsActions.pathSelected({ selectedPathId: chain.id }));
        dispatch(pathsActions.hideAll(undefined));
        dispatch(pathsActions.zoomToPath(chain.id));
        const chainTsps = getUniqueChainTsps(scenario, chain);
        setTspFilter(chainTsps);
      }
    }
  }, [
    queryParams,
    scenario?.operation_chains,
    allNodes,
    allEdges,
    allPoints,
    tsps,
  ]);

  const dispatch = useDispatch();
  // const params = useParams();
  // const { scenarioGroupId } = params;
  // const groupUrlPrepend = scenarioGroupId
  //   ? `/scenarios/groups/${scenarioGroupId}`
  //   : '';

  const totalProfit = filteredChains.reduce(
    (sum, chain) => Number(sum) + Number(chain.profit),
    0,
  );
  // filter out kexchanges so we just see the total delivered dth
  // TODO: this sorting and filtering should be done in backend
  const totalVolume = filteredChains.reduce(
    (sum, chain) => Number(sum) + Number(chain.delivered_dth),
    0,
  );

  const optimizeScenario = async ({ update = false } = {}) => {
    try {
      analytics.logEvent(AnalyticsEvent.OPTIMIZED_SCENARIO, {
        scenario: JSON.stringify({
          scenarioId: scenario.id,
          gasDate: scenario.flow_date,
        }),
      });
      if (update) await updateScenario();
      if (queryParams.get('sync') === 'true') {
        const result = await createUpdateScenario({
          ...store.getState().scenario.data,
          optimize: true,
        }).unwrap();
        if (result) {
          onOptimize(result);
        } else {
          toast.error('Something went wrong, please contact support');
        }
      } else {
        asyncOptimize();
      }
    } catch (e) {
      toast.error('Something went wrong, please contact support');
      console.error(e);
    }

    /**
     * This could be replaced by a setInterval, but I wanted to make sure
     * that the next update doesn't start until the previous one is finished
     */
    if (shouldLiveUpdate) {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      timerRef.current = setTimeout(async () => {
        await optimizeScenario({ update: true });
      }, 30 * SECOND);
    }
  };

  const onOptimizationStateChange = async (newState: OptimizationTaskState) => {
    if (newState === 'SUCCESS') {
      toast.success('Optimization complete');
      const result = await scenarioTrigger({ scenarioId: scenario.id });
      if ('data' in result && result.data) {
        onOptimize(result.data);
      }
    }
  };

  useEffect(() => {
    onOptimizationStateChange(state);
  }, [state]);

  useEffect(() => {
    if (scenario.id !== 'new') {
      const stop = watch();
      return stop;
    }
    return undefined;
  }, [watch, scenario?.id]);

  const updateScenario = async (flowDate = toIsoDate(new Date())) => {
    const promises = [
      updatePointConstraints(flowDate),
      updateSegmentConstraints(flowDate),
      updatePrices(flowDate),
    ];
    await Promise.all(promises);
    dispatch(
      scenarioActions.setFlowDate({
        flow_date: flowDate,
      }),
    );
  };

  const updatePointConstraints = async (flowDate: string) => {
    try {
      const result = await pointConstraintsTrigger({
        date: flowDate,
      }).unwrap();
      await dispatch(
        scenarioActions.overwrite({
          componentType: 'point_constraints',
          components: result,
          // applySettings: true,
        }),
      );
    } catch (e) {
      console.error(e);
    }
  };

  const updateSegmentConstraints = async (flowDate: string) => {
    try {
      const result = await segmentConstraintsTrigger({
        date: flowDate,
      }).unwrap();
      await dispatch(
        scenarioActions.overwrite({
          componentType: 'segment_constraints',
          components: result,
          // applySettings: true,
        }),
      );
    } catch (e) {
      console.error(e);
    }
  };

  const updatePrices = async (flowDate: string) => {
    try {
      const marketPrices: Market[] = await pricesTrigger({
        date: flowDate,
        priceSource: scenario?.settings?.price_source || 'POSTMORTEM_DAILY',
        bidAsk: scenario?.settings?.price_type === 'BID_ASK',
      }).unwrap();

      await dispatch(
        scenarioActions.overwrite({
          componentType: 'markets',
          components: marketPrices.filter((market) => market.price !== null),
          // applySettings: true,
        }),
      );
    } catch (e) {
      console.error(e);
    }
  };

  const exportScenario = () => {
    const wb = scenarioToWorkbook(scenario, allEdges, allPoints, allNodes);
    xlsx.writeFile(wb, `${scenario.name}.xlsx`);
  };

  const copyLink = () => {
    const url = `${window.location.origin}/scenarios/${scenario.id}`;
    copyToClipboard(url, 'text/plain');
    toast.success('Scenario link copied to clipboard');
  };

  useEffect(() => {
    if (shouldLiveUpdate) {
      optimizeScenario({ update: true });
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [shouldLiveUpdate]);

  const isUpdating =
    isGetScenarioFetching ||
    isGetScenarioLoading ||
    state === 'STARTED' ||
    isCreatingUpdating ||
    fetchingPrices ||
    fetchingPointConstraints ||
    fetchingSegmentConstraints;

  const setLockState = useCallback(
    async (locked: boolean) => {
      const lockRes = await lockScenarioTrigger({
        scenarioId: scenario.id,
        lockState: locked,
      });
      if ('error' in lockRes && lockRes.error) {
        toast.error('Something went wrong, please contact support');
        return;
      }
      dispatch(scenarioActions.setLockState({ locked }));
    },
    [scenario],
  );

  const duplicateScenario = useCallback(() => {
    console.log('duplicating scenario', scenario);
    navigate(`/scenarios/new`, {
      state: {
        scenario: {
          ...scenario,
          name: `${scenario.name} Copy`,
          previous_version: scenario.id,
          id: 'new',
        },
      },
    });
  }, [navigate, scenario]);

  const lockedLoading = scenario.id === 'new' || isLocking || isUpdating;

  return (
    <>
      {scenarioGroup && <h5>{scenarioGroup.name}</h5>}
      <UnflowedGasWarnings scenario={scenario} />
      <h5 className="d-flex align-content-center justify-content-between w-100">
        {scenario.name || `Scenario #${scenario.id}`}{' '}
        <div className="float-end" style={{ flexShrink: '0' }}>
          <span id="share-scenario-icon">
            <LinkIcon
              role="button"
              onClick={copyLink}
              color={colors.primaryBlue}
            />
          </span>
          <UncontrolledTooltip target="share-scenario-icon">
            Copy Scenario Link
          </UncontrolledTooltip>
          <span id="export-scenario-icon">
            <UploadIcon
              role="button"
              onClick={exportScenario}
              color={colors.primaryBlue}
            />
          </span>
          <UncontrolledTooltip target="export-scenario-icon">
            Export Scenario
          </UncontrolledTooltip>
          {!scenario.readonly && (
            <>
              <span id="lock-state-icon">
                {scenario.locked ? (
                  <>
                    <LockIcon
                      id="locked-icon"
                      role={lockedLoading ? 'note' : 'button'}
                      color={
                        lockedLoading ? colors.lightGrey : colors.primaryBlue
                      }
                      onClick={() => setLockState(false)}
                    />
                  </>
                ) : (
                  <>
                    <LockUnlockedIcon
                      id="unlocked-icon"
                      role={lockedLoading ? 'note' : 'button'}
                      color={
                        lockedLoading ? colors.lightGrey : colors.primaryBlue
                      }
                      onClick={() => setLockState(true)}
                    />
                  </>
                )}
              </span>
              <UncontrolledTooltip target="lock-state-icon">
                {scenario.locked ? 'Unlock scenario' : 'Lock scenario'}
              </UncontrolledTooltip>
            </>
          )}
          <span id="copy-scenario-icon">
            <CopyIcon
              data-testid="duplicate-button"
              role="button"
              color={colors.primaryBlue}
              onClick={duplicateScenario}
            />
            <UncontrolledTooltip target="copy-scenario-icon">
              Duplicate scenario
            </UncontrolledTooltip>
          </span>
          {!scenario.readonly && viewMode === 'development' && (
            <AddToGroup scenarioId={Number(scenario.id)}>
              Add to group
            </AddToGroup>
          )}
        </div>
      </h5>
      <div className="clearfix" />
      <p
        style={{
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          maxWidth: '100%',
        }}
      >
        {scenario.description}
      </p>
      <div>
        <strong>Flow Date:</strong>{' '}
        {new Date(scenario.flow_date).toLocaleDateString('en-US', {
          timeZone: 'UTC',
        })}
      </div>

      {scenario.operation_chains.length > 0 && (
        <>
          <div>
            <strong>Total Profit:</strong> ${numberFormatter(totalProfit)}
            {filteredChains.length < scenario.operation_chains.length && <>*</>}
          </div>
          <div>
            <strong>Total Delivered Volume:</strong>{' '}
            {numberFormatter(Math.floor(totalVolume))} dth
            {filteredChains.length < scenario.operation_chains.length && <>*</>}
          </div>
          <div>
            <strong>Profit/Dth:</strong> $
            {totalVolume ? numberFormatter(totalProfit / totalVolume, 3) : '-'}
            {filteredChains.length < scenario.operation_chains.length && <>*</>}
          </div>
          {filteredChains.length < scenario.operation_chains.length && (
            <p className="text-muted">
              *Profit and volume are based on the partial selection of filtered
              chains
            </p>
          )}
        </>
      )}
      <hr />
      <div className="d-grid gap-2">
        <div id="optimize-button-group" className="d-grid gap-2">
          <OptimizeScenarioButton
            scenarioId={scenarioId}
            scenario={scenario}
            disabled={scenario.locked}
            isUpdating={isUpdating}
            shouldLiveUpdate={shouldLiveUpdate}
            toggleLiveUpdate={(evt) => setShouldLiveUpdate(evt.target.checked)}
            onClick={(update) => {
              if (timerRef.current) {
                clearTimeout(timerRef.current);
              }
              optimizeScenario({ update });
            }}
          />
          {scenario?.readonly !== true && (
            <button
              onClick={() => onEditStart()}
              className="btn btn-warning w-100"
              disabled={scenario.locked || state === 'STARTED'}
            >
              Edit Scenario
            </button>
          )}
        </div>
        {optimizationProgress && !optimizationError && state === 'STARTED' && (
          <ProgressBar percent={optimizationProgress.percent} />
        )}
        {optimizationError ? (
          <p className="text-danger text-sm mt-0 mb-0">{optimizationError}</p>
        ) : (
          viewMode === 'development' && (
            <p className="text-muted text-sm mt-0 mb-0">
              <DevelopmentFlag className="me-1" />
              {message}
              {optimizationProgress && (
                <span>
                  : {optimizationProgress?.step} (
                  {optimizationProgress?.completed_steps}/
                  {optimizationProgress?.total_steps})
                </span>
              )}
            </p>
          )
        )}
        {scenario.locked && (
          <UncontrolledTooltip placement="top" target="optimize-button-group">
            This scenario is currently locked <LockIcon />
          </UncontrolledTooltip>
        )}
        {scenario.previous_version && (
          <div className="btn-group" role="group">
            <NavLink
              className="btn btn-outline-primary w-100"
              to={`/scenarios/${scenario.previous_version}`}
            >
              Previous Version
            </NavLink>
          </div>
        )}
      </div>
    </>
  );
};
